import { useEffect, useState } from "react";
import Icon from "./Icon";

const DISMISS_KEY = "pwa-install-dismissed-at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const isMobile = () => {
  if (typeof navigator === "undefined") return false;
  if (navigator.userAgentData?.mobile) return true;
  return /Android|iPhone|iPad|iPod|Mobile|webOS|BlackBerry/i.test(navigator.userAgent || "");
};

const isIos = () =>
  typeof navigator !== "undefined" && /iPhone|iPad|iPod/i.test(navigator.userAgent || "");

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  return window.navigator?.standalone === true; // iOS-only flag
};

const recentlyDismissed = () => {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return at > 0 && Date.now() - at < DISMISS_TTL_MS;
  } catch {
    return false;
  }
};

export default function PwaInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (!isMobile() || isStandalone() || recentlyDismissed()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari never fires beforeinstallprompt, so surface the manual
    // instructions after a short delay if we're on iOS and not installed.
    let iosTimer;
    if (isIos()) {
      iosTimer = setTimeout(() => {
        setIosHint(true);
        setVisible(true);
      }, 2000);
    }

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* noop */
    }
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    setInstalling(true);
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") {
        setVisible(false);
      } else {
        dismiss();
      }
    } catch {
      dismiss();
    } finally {
      setInstalling(false);
      setDeferred(null);
    }
  };

  return (
    <>
      <div
        onClick={dismiss}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.45)",
          zIndex: 1000,
        }}
      />
      <div
        role="dialog"
        aria-labelledby="pwa-install-title"
        style={{
          position: "fixed",
          left: 12,
          right: 12,
          bottom: "max(12px, env(safe-area-inset-bottom))",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 16,
          boxShadow: "0 20px 50px rgba(11, 20, 55, 0.25)",
          padding: 18,
          zIndex: 1001,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "var(--primary-50)",
              color: "var(--primary-700)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon name="download" size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              id="pwa-install-title"
              style={{ fontSize: 15, fontWeight: 600, color: "var(--ink-900)" }}
            >
              Install SMART CA
            </div>
            <div style={{ fontSize: 13, color: "var(--ink-600)", marginTop: 4, lineHeight: 1.45 }}>
              {iosHint
                ? "Tap the Share button, then choose 'Add to Home Screen' to install the app for faster access and offline support."
                : "Get faster access, push notifications, and a full-screen experience by installing the app on your device."}
            </div>
          </div>
          <button
            className="icon-btn"
            onClick={dismiss}
            title="Dismiss"
            aria-label="Dismiss install prompt"
            style={{ flexShrink: 0 }}
          >
            <Icon name="x" size={16} />
          </button>
        </div>

        {!iosHint && (
          <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
            <button className="btn btn-ghost" onClick={dismiss} disabled={installing}>
              Not now
            </button>
            <button className="btn btn-primary" onClick={install} disabled={installing || !deferred}>
              <Icon name="download" size={13} /> {installing ? "Installing…" : "Install"}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
